import React, { useEffect, useState } from "react";
import { Box, Grid, IconButton, Tooltip } from "@mui/material";
import FlexBetween from "../../components/FlexBetween";
import DataTable from "../../components/DataGrids/DataGrid";
import { GridColDef } from "@mui/x-data-grid";
import { useNavigate, useSearchParams } from "react-router-dom";
import ButtonComponent from "../../components/Button.Component";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import AddSharpIcon from "@mui/icons-material/AddSharp";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import {
  deleteRunInstance,
  reactivateRunInstance,
} from "../../services/runInstance";
import useFetchPaginatedRunInstance from "../../hooks/useFetchPaginatedRunInstance";
import { getFormattedDateTime } from "../../services/utils";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import {
  IconDeleteBlueSvg,
  IconEditBase,
  IconEyeBase,
  IconCheckCircleIconGreenSvg,
  IconCrossCircleIconSvg,
  IconReactivateSvg,
} from "../../common/utils/icons";
import { useToast } from "../../services/utils";
import Loader from "../../components/Molecules/Loader/Loader";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import ConfirmDailog from "../../components/Dialogs/ConfirmDailog";
import SearchFilterBar from "../../components/Molecules/FilterSearch/SearchFilterBar";
import useDebounce from "../../hooks/useDebounce";
import { useAppTranslation } from "../../hooks/useAppTranslation";

// Centralized Filter Configuration for Execution Group List
const RUN_INSTANCE_FILTER_CONFIG = {
  // Default filter state - single source of truth
  DEFAULT_STATE: {
    run_name: "",
    status_filter: "Active",
  },

  // Fields to persist in URL
  get URL_FIELDS() {
    return Object.keys(this.DEFAULT_STATE);
  },

  // Field type mappings for URL serialization/deserialization
  FIELD_TYPES: {
    ID_FIELDS: [],
    STRING_FIELDS: ["run_name", "status_filter"],
    DATE_FIELDS: [],
  },
} as const;

const RunInstance: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const { t } = useAppTranslation();
  //for pagination default values and states
  const defaultPage = process.env.REACT_APP_DEFAULT_PAGE;
  const { rowsPerPage } = useRowsPerPage();
  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1,
  );
  const [pSize, setPSize] = useState<number>(rowsPerPage);
  // states
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState<boolean>(false);
  const [fileData, setFileData] = useState<any>([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [runInstanceToDelete, setRunInstanceToDelete] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // SearchFilterBar state - initialize from URL parameters
  const [searchFilterData, setSearchFilterData] = useState<any>(() => {
    const initialState: any = {
      ...RUN_INSTANCE_FILTER_CONFIG.DEFAULT_STATE,
    };

    // Restore filters from URL parameters
    RUN_INSTANCE_FILTER_CONFIG.URL_FIELDS.forEach((field: string) => {
      const urlValue = searchParams.get(field);
      if (urlValue) {
        initialState[field] = urlValue;
      }
    });

    return initialState;
  });

  // Extract filters from searchFilterData
  const effectiveStatusFilter = searchFilterData?.status_filter || "";
  const effectiveRunName = searchFilterData?.run_name || "";

  // Debounce run_name with 400ms delay
  const debouncedRunName = useDebounce(effectiveRunName);

  // Determine if we should include run_name in API call (minimum 3 characters)
  const effectiveRunNameSearch =
    debouncedRunName.trim().length >= 3 ? debouncedRunName : "";

  // hooks
  const [fetchedRunInstance] = useFetchPaginatedRunInstance({
    setIsLoading: setIsDownloadLoading,
    page,
    pSize,
    searchTerm: effectiveRunNameSearch,
    statusFilter: effectiveStatusFilter,
  });

  // Sync filters to URL parameters (state → URL)
  useEffect(() => {
    const newParams = new URLSearchParams();

    RUN_INSTANCE_FILTER_CONFIG.URL_FIELDS.forEach((field) => {
      const value = searchFilterData[field];
      if (value !== null && value !== undefined && value !== "") {
        // Don't add default values to URL to keep it clean
        if (field === "status_filter" && value === "Active") {
          return; // Skip default status filter
        }
        newParams.set(field, String(value));
      }
    });

    // Only update URL if params have changed
    const currentParamsString = searchParams.toString();
    const newParamsString = newParams.toString();
    if (currentParamsString !== newParamsString) {
      setSearchParams(newParams, { replace: true });
    }
  }, [searchFilterData, searchParams, setSearchParams]);

  // Update filter state when URL changes (URL → state for back/forward navigation)
  useEffect(() => {
    const urlFilterState: any = {
      ...RUN_INSTANCE_FILTER_CONFIG.DEFAULT_STATE,
    };

    // Extract filter parameters from URL
    RUN_INSTANCE_FILTER_CONFIG.URL_FIELDS.forEach((field: string) => {
      const urlValue = searchParams.get(field);
      if (urlValue) {
        urlFilterState[field] = urlValue;
      }
    });

    // Only update state if URL differs from current state
    const hasChanges = RUN_INSTANCE_FILTER_CONFIG.URL_FIELDS.some(
      (field) => urlFilterState[field] !== searchFilterData[field],
    );

    if (hasChanges) {
      setSearchFilterData(urlFilterState);
    }
  }, [searchParams]);

  // Reset page to 1 when search filter changes
  useEffect(() => {
    if (
      searchFilterData?.run_name !== undefined ||
      searchFilterData?.status_filter !== undefined
    ) {
      setPage(1);
    }
  }, [searchFilterData]);

  const handelClickEvent = () => {
    navigate("/run-instance/add");
  };

  // Handle delete execution group
  const handleDeleteRunInstance = (runInstance: any) => {
    setRunInstanceToDelete(runInstance);
    setDeleteConfirmOpen(true);
  };

  // Handle reactivate execution group
  const handleReactivate = async (id: number, runName: string) => {
    setIsLoading(true);
    try {
      await reactivateRunInstance(id);
      showToast(
        t("executionGroups.toast.reactivated", { name: runName }),
        "success",
      );

      // Optimistic UI update - immediately update the is_active flag in local state
      setFileData((prev: any) => ({
        ...prev,
        items: prev?.items?.map((item: any) =>
          item.id === id ? { ...item, is_active: true } : item,
        ),
      }));
    } catch (error) {
      showToast(t("executionGroups.toast.failedReactivate"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Confirm delete action
  const confirmDelete = async () => {
    if (!runInstanceToDelete) return;

    try {
      setDeleteLoading(true);
      const result = await deleteRunInstance(runInstanceToDelete.id);
      if (result && result.status === 200) {
        showToast(
          t("executionGroups.toast.deleted", {
            name: runInstanceToDelete.run_name,
          }),
          "success",
        );
        setDeleteConfirmOpen(false);
        setRunInstanceToDelete(null);
        // Update the execution group list to remove the deleted item
        const updatedRunInstanceData = fileData?.items?.filter(
          (run_instance: { id: any }) =>
            run_instance.id !== runInstanceToDelete.id,
        );
        setFileData((prev: any) => ({
          ...prev,
          items: updatedRunInstanceData,
        }));
      }
    } catch (error: any) {
      showToast(
        error?.message || t("executionGroups.toast.failedDelete"),
        "error",
      );
    } finally {
      setDeleteLoading(false);
      setDeleteConfirmOpen(false);
    }
  };

  const columns: GridColDef[] = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      width: 1,
      minWidth: 20,
      flex: 0,
      renderCell: (params: any) => <></>,
      cellClassName: "no-padding-cell",
      headerClassName: "no-padding-cell",
    },
    {
      field: "run_id",
      headerName: t("executionGroups.list.columns.executionGroupId"),
      minWidth: 80,
      flex: 1,
      renderCell: (params: any) => {
        return <span>{params.id}</span>;
      },
    },
    {
      field: "run_name",
      headerName: t("executionGroups.list.columns.executionGroupName"),
      minWidth: 140,
      flex: 1,
      renderCell: (params: any) => {
        return <LimitChractersWithTooltip value={params?.value} />;
      },
    },
    // {
    //   field: "modified_by_user",
    //   headerName: "Modified By",
    //   minWidth: 140,
    //   flex: 1,
    //   renderCell: (params: any) => {
    //     return params.value?.username;
    //   },
    // },
    {
      field: "updated_on",
      headerName: t("executionGroups.list.columns.lastModified"),
      minWidth: 180,
      flex: 1,
      renderCell: (params: any) => {
        return getFormattedDateTime(params.value);
      },
    },
    {
      field: "is_active",
      headerName: t("executionGroups.list.columns.status"),
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params: any) => {
        return params.value ? (
          <Tooltip title={t("common.status.active")} placement="top" arrow>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconCheckCircleIconGreenSvg width={21} height={21} />
            </Box>
          </Tooltip>
        ) : (
          <Tooltip title={t("common.status.inactive")} placement="top" arrow>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconCrossCircleIconSvg width={21} height={21} />
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "action",
      headerName: t("executionGroups.list.columns.actions"),
      align: "center",
      headerAlign: "center",
      width: 210,
      renderCell: (params: any) => {
        const handleDelete = () => {
          handleDeleteRunInstance(params.row);
        };
        const isActive = params.row.is_active === true;

        return (
          <>
            {/* For active records, show all action buttons */}
            {isActive && (
              <>
                <Tooltip
                  title={t("executionGroups.actions.view")}
                  placement="top"
                  arrow
                >
                  <IconButton
                    className="datagrid-action-btn"
                    color="inherit"
                    onClick={() => {
                      navigate(`/run-instance/view/${params.row.id}`);
                    }}
                  >
                    <IconEyeBase />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={t("executionGroups.actions.edit")}
                  placement="top"
                  arrow
                >
                  <IconButton
                    className="datagrid-action-btn"
                    color="inherit"
                    onClick={() => {
                      navigate(`/run-instance/edit/${params.row.id}`);
                    }}
                  >
                    <IconEditBase />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title="View Run Scoring Overview"
                  placement="top"
                  arrow
                >
                  <IconButton
                    className="datagrid-action-btn"
                    color="inherit"
                    onClick={() => {
                      navigate(`/scoring/run-overview/${params.row.id}`);
                    }}
                  >
                    <AssessmentOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete Execution Group" placement="top" arrow>
                  <IconButton
                    className="datagrid-action-btn"
                    color="inherit"
                    onClick={() => handleDelete()}
                  >
                    <IconDeleteBlueSvg />
                  </IconButton>
                </Tooltip>
              </>
            )}

            {/* For inactive records, show ONLY Reactivate button */}
            {!isActive && (
              <Tooltip
                title={t("executionGroups.actions.reactivate")}
                placement="top"
                arrow
              >
                <IconButton
                  className="datagrid-action-btn"
                  color="inherit"
                  onClick={() =>
                    handleReactivate(params.row.id, params.row.run_name)
                  }
                >
                  <IconReactivateSvg />
                </IconButton>
              </Tooltip>
            )}
            {/* <DropdownMenu
              dropdownMenuItems={[
                <IconButton
                  key="export"
                  className="datagrid-action-btn"
                  color="inherit"
                  onClick={() =>
                    downloadRunInstanceExportFile(
                      params.row.id,
                      setIsDownloadLoading
                    )
                  }
                >
                  <IconExportIconBlue />
                  Export
                </IconButton>,
              ]}
            /> */}
          </>
        );
      },
    },
  ];

  // for update Execution Group list data
  useEffect(() => {
    if (fetchedRunInstance) {
      setFileData(fetchedRunInstance);
    }
  }, [fetchedRunInstance]);

  return (
    <Box>
      {isDownloadLoading && <Loader isLoading={isDownloadLoading} />}
      {deleteLoading && <Loader isLoading={deleteLoading} zIndex={1400} />}
      <ConfirmDailog
        isDailogOpen={deleteConfirmOpen}
        handleCancelAction={() => setDeleteConfirmOpen(false)}
        handleConfirmAction={confirmDelete}
        dailogTitle={t("executionGroups.dialog.deleteTitle")}
        dailogDescription={t("executionGroups.dialog.deleteDescription", {
          name: runInstanceToDelete?.run_name,
        })}
      />
      <Box className="text-box-card list-page-card compact-text-box-card">
        <Grid container rowSpacing={2.5} columnSpacing={2}>
          <Grid item xs>
            <SearchFilterBar
              largeWidth={true}
              setSearchFilterData={setSearchFilterData}
              searchFilterData={searchFilterData}
              requiredFields={["run_name", "status_filter"]}
              primaryFields={["run_name", "status_filter"]}
              fieldRequirements={{
                defaultStatusFilter: "Active", // Reset to Active status on Clear Filter
              }}
            />
          </Grid>
          <Grid
            item
            xs="auto"
            sx={{ display: "flex", alignItems: "flex-start" }}
          >
            <ButtonComponent
              handelClickEvent={handelClickEvent}
              className="btn-orange"
            >
              <AddSharpIcon sx={{ marginRight: "4px" }} />{" "}
              {t("executionGroups.actions.add")}
            </ButtonComponent>
          </Grid>
        </Grid>
      </Box>

      <FlexBetween>
        <DataTable
          dataColumns={columns}
          dataRows={fileData?.items || []}
          loading={isLoading}
          dataListTitle={t("executionGroups.list.title")}
          className="dataTable no-radius hide-progress-icon"
          paginationMode="server"
          rowCount={fileData?.total}
          paginationModel={{
            page: page - 1,
            pageSize: pSize,
          }}
          onPaginationModelChange={(params: any) => {
            if (params.pageSize !== pSize || params.page !== page - 1) {
              setPage(params.page + 1);
              setPSize(params.pageSize);
            }
          }}
          singlePageMaxHeightDiff={368}
        />
      </FlexBetween>
    </Box>
  );
};

export default RunInstance;
